import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bell,
  ChevronRight,
  HelpCircle,
  Menu,
  PanelRight,
  PanelRightClose,
  Phone,
  Pin,
  Search,
  Settings,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import type { Room } from "@/lib/types";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Avatar } from "@/components/ui/Avatar";
import { cn } from "@/lib/cn";
import {
  openCommandPalette,
  toggleSidebar,
  toggleContext,
  useContextOpen,
  toggleHelpDialog,
} from "@/lib/uiState";
import { useNavigate } from "react-router-dom";
import { useRooms } from "@/lib/rooms";
import { useSession } from "@/lib/auth";
import { NotificationDialog } from "@/features/notifications/NotificationDialog";

/* -------------------------------------------------------------------------- */
/* Public types                                                               */
/* -------------------------------------------------------------------------- */

export interface TopBarProps {
  /** When set, the title/breadcrumb resolves from the room. */
  room?: Room;
  /** Extra trailing actions (e.g. a custom CTA). Rendered before the system icons. */
  trailing?: ReactNode;
  /** Override the auto-derived heading. */
  title?: string;
  /** Override the auto-derived sub-heading. */
  subtitle?: string;
  /** Unread notification count for the bell icon. */
  notificationCount?: number;
}

/* -------------------------------------------------------------------------- */
/* TopBar                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Sticky application header.
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ [☰] Breadcrumb                  Search             🔔 ❓ ⚙ 👤 │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * - Mobile: hamburger toggles the sidebar drawer.
 * - Center: a click-to-open CommandPalette trigger (`Ctrl+K`).
 * - Right: notifications · help · settings · user menu, plus right-pane toggle.
 */
export function TopBar({
  room,
  trailing,
  title,
  subtitle,
  notificationCount: count,
}: TopBarProps): JSX.Element {
  const { t } = useTranslation();
  const allowContext = /^\/(?:rooms|channels|dm)\/[^/]+/.test(useLocation().pathname);
  const session = useSession();
  const { rooms, loading: roomsLoading, error: roomsError, refresh: refreshRooms } = useRooms();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationCount = count ?? rooms.reduce((sum, item) => sum + item.unreadCount, 0);
  const navigate = useNavigate();
  const [contextOpen] = useContextOpen();
  const heading = title ?? room?.name ?? "—";
  const sub =
    subtitle ??
    (room ? (room.kind === "channel" ? t("nav.rooms") : t("nav.dm")) : undefined);

  return (
    <>
    <header
      className={cn(
        "sticky top-0 z-20 h-14 shrink-0",
        "px-3 md:px-4 flex items-center gap-2",
        "bg-canvas/95 backdrop-blur-sm border-b border-border-subtle",
      )}
    >
      {/* ---------- Mobile hamburger ---------- */}
      <button
        type="button"
        aria-label={t("shortcuts.toggleSidebar")}
        onClick={() => toggleSidebar()}
        className={cn(
          "p-1.5 rounded-md text-fg-secondary md:hidden",
          "hover:bg-surface-raised hover:text-fg-primary",
          "transition-colors duration-150 ease-out",
          "focus-visible:shadow-focus",
        )}
      >
        <Menu size={18} aria-hidden />
      </button>

      {/* ---------- Heading / breadcrumb ---------- */}
      <div className="min-w-0 flex-1 flex items-center gap-3">
        <Breadcrumb fallbackTitle={heading} room={room} />
        {sub && !room ? (
          <p className="hidden md:block text-xs text-fg-muted truncate">{sub}</p>
        ) : null}
      </div>

      {/* ---------- Center search (md+) ---------- */}
      <button
        type="button"
        onClick={openCommandPalette}
        className={cn(
          "hidden md:flex items-center gap-2 h-9 px-3 w-72 lg:w-96 rounded-md",
          "bg-surface-base border border-border-default text-fg-muted text-sm",
          "hover:bg-surface-raised hover:border-border-strong/40",
          "transition-colors duration-150 ease-out",
          "focus-visible:shadow-focus",
        )}
        aria-label={t("search.placeholder")}
      >
        <Search size={14} aria-hidden />
        <span className="flex-1 text-left truncate">{t("search.placeholder")}</span>
        <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-canvas border border-border-subtle font-mono shrink-0">
          Ctrl K
        </kbd>
      </button>

      {/* ---------- Mobile search icon ---------- */}
      <button
        type="button"
        aria-label={t("search.placeholder")}
        onClick={openCommandPalette}
        className={cn(
          "md:hidden p-1.5 rounded-md text-fg-secondary",
          "hover:bg-surface-raised hover:text-fg-primary",
          "transition-colors duration-150 ease-out",
          "focus-visible:shadow-focus",
        )}
      >
        <Search size={18} aria-hidden />
      </button>

      {/* ---------- Room-scoped CTA ---------- */}
      {room ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leadingIcon={<Phone size={14} aria-hidden />}
          className="hidden md:inline-flex"
          onClick={() => navigate(`/calls/${encodeURIComponent(room!.id)}`)}
        >
          {t("call.start")}
        </Button>
      ) : null}

      {trailing}

      {/* ---------- Notification bell ---------- */}
      <IconAction
        label={t("nav.notifications", "通知")}
        title={`${t("nav.notifications", "通知")} (${notificationCount})`}
        onClick={() => { setNotificationsOpen(true); void refreshRooms(); }}
        ariaHasPopup="dialog"
        ariaExpanded={notificationsOpen}
      >
        <Bell size={16} aria-hidden />
        {notificationCount > 0 ? (
          <span
            aria-hidden
            className="absolute top-0.5 right-0.5 min-w-4 h-4 px-1 rounded-full bg-danger text-fg-inverse text-[10px] font-medium flex items-center justify-center"
          >
            {notificationCount > 9 ? "9+" : notificationCount}
          </span>
        ) : null}
        <span className="sr-only">
          {notificationCount > 0
            ? `${notificationCount} ${t("home.unreadMentions")}`
            : t("home.noActivity")}
        </span>
      </IconAction>

      {/* ---------- Help ---------- */}
      <IconAction
        label={t("shortcuts.help")}
        title={t("shortcuts.help")}
        onClick={() => toggleHelpDialog(true)}
      >
        <HelpCircle size={16} aria-hidden />
      </IconAction>

      {/* ---------- Settings ---------- */}
      <Link
        to="/settings"
        aria-label={t("nav.settings")}
        title={t("nav.settings")}
        className={cn(
          "relative p-1.5 rounded-md text-fg-secondary",
          "hover:bg-surface-raised hover:text-fg-primary",
          "transition-colors duration-150 ease-out",
          "focus-visible:shadow-focus",
          "hidden sm:inline-flex",
        )}
      >
        <Settings size={16} aria-hidden />
      </Link>

      {/* ---------- Right-pane toggle ---------- */}
      {allowContext && <IconAction
        label={t("shortcuts.toggleContext")}
        title={t("shortcuts.toggleContext")}
        onClick={() => toggleContext()}
        ariaPressed={contextOpen}
      >
        {contextOpen ? (
          <PanelRightClose size={16} aria-hidden />
        ) : (
          <PanelRight size={16} aria-hidden />
        )}
      </IconAction>}

      {/* ---------- User avatar (decorative on mobile) ---------- */}
      <Link
        to="/settings"
        aria-label={(session?.user.displayName || "アカウント")}
        title={(session?.user.displayName || "アカウント")}
        className="ml-1 rounded-full focus-visible:shadow-focus shrink-0"
      >
        <Avatar name={(session?.user.displayName || "アカウント")} size={28} imageUrl={session?.user.avatarUrl} />
      </Link>
    </header>
    <NotificationDialog
      open={notificationsOpen}
      onClose={() => setNotificationsOpen(false)}
      rooms={rooms}
      loading={roomsLoading}
      error={roomsError}
      onRefresh={() => { void refreshRooms(); }}
    />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Breadcrumb                                                                 */
/* -------------------------------------------------------------------------- */

interface BreadcrumbCrumb {
  label: string;
  to?: string;
}

function Breadcrumb({
  fallbackTitle,
  room,
}: {
  fallbackTitle: string;
  room?: Room;
}): JSX.Element {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const crumbs = useCrumbs(pathname, t, room, fallbackTitle);

  if (crumbs.length === 0) {
    return (
      <h1 className="text-md font-bold truncate flex items-center gap-2">
        {fallbackTitle}
        {room?.pinned ? (
          <Pin size={12} aria-hidden className="text-fg-muted shrink-0" />
        ) : null}
      </h1>
    );
  }

  const last = crumbs[crumbs.length - 1]!;
  const head = crumbs.slice(0, -1);

  return (
    <nav aria-label="Breadcrumb" className="min-w-0 flex items-center gap-1.5">
      <ol className="flex items-center gap-1.5 min-w-0">
        {head.map((c, i) => (
          <li key={`${c.label}-${i}`} className="flex items-center gap-1.5 min-w-0">
            {c.to ? (
              <Link
                to={c.to}
                className={cn(
                  "text-sm text-fg-muted hover:text-fg-secondary hover:underline truncate",
                  "transition-colors duration-150 ease-out",
                )}
              >
                {c.label}
              </Link>
            ) : (
              <span className="text-sm text-fg-muted truncate">{c.label}</span>
            )}
            <ChevronRight size={12} aria-hidden className="text-fg-subtle shrink-0" />
          </li>
        ))}
        <li className="min-w-0 flex items-center gap-2">
          <h1
            aria-current="page"
            className="text-md font-bold text-fg-primary truncate"
          >
            {last.label}
          </h1>
          {room?.pinned ? (
            <Pin size={12} aria-hidden className="text-fg-muted shrink-0" />
          ) : null}
          {room ? (
            <Badge tone="neutral" className="hidden md:inline-flex text-[10px] px-1.5 py-0">
              {room.kind === "channel" ? t("nav.rooms") : t("nav.dm")}
            </Badge>
          ) : null}
        </li>
      </ol>
    </nav>
  );
}

function useCrumbs(
  pathname: string,
  t: (k: string) => string,
  room: Room | undefined,
  fallback: string,
): BreadcrumbCrumb[] {
  // Hand-curated mapping for the routes registered in App.tsx.
  if (pathname === "/" || pathname === "") {
    return [{ label: t("nav.home") }];
  }
  if (pathname.startsWith("/rooms")) {
    return [
      { label: t("nav.rooms"), to: "/" },
      { label: room?.name?.replace(/^#/, "") ?? fallback },
    ];
  }
  if (pathname.startsWith("/tasks")) {
    return [{ label: t("nav.tasks") }];
  }
  if (pathname.startsWith("/files")) {
    return [{ label: t("nav.files") }];
  }
  if (pathname.startsWith("/calls")) {
    return [{ label: fallback === "—" ? t("call.start") : fallback }];
  }
  if (pathname.startsWith("/settings")) {
    const seg = pathname.split("/").filter(Boolean);
    if (seg.length > 1) {
      return [
        { label: t("nav.settings"), to: "/settings" },
        { label: fallback },
      ];
    }
    return [{ label: t("nav.settings") }];
  }
  return [{ label: fallback }];
}

/* -------------------------------------------------------------------------- */
/* Right-aligned icon action                                                  */
/* -------------------------------------------------------------------------- */

function IconAction({
  label,
  title,
  onClick,
  ariaPressed,
  ariaHasPopup,
  ariaExpanded,
  children,
}: {
  label: string;
  title?: string;
  onClick: () => void;
  ariaPressed?: boolean;
  ariaHasPopup?: "dialog";
  ariaExpanded?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={title ?? label}
      aria-pressed={ariaPressed}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      className={cn(
        "relative p-1.5 rounded-md text-fg-secondary",
        "hover:bg-surface-raised hover:text-fg-primary",
        "transition-colors duration-150 ease-out",
        "focus-visible:shadow-focus",
      )}
    >
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Re-exports                                                                 */
/* -------------------------------------------------------------------------- */

export function PlanBadge({
  plan,
}: {
  plan: "free" | "starter" | "business" | "enterprise";
}): JSX.Element {
  return <Badge tone={plan === "free" ? "neutral" : "brand"}>{plan}</Badge>;
}
